import chatService from "../services/chat.service";
import chatSocketService from "../services/chatSocket.service";
import { backendUrl } from "../Router/constant";
import { clearAuthenticationStorage } from "../utils/authStorage";
import {
  CHAT_ERROR,
  CHAT_DETAIL,
  CHAT_DETAIL_LOADING,
  CHAT_LIST,
  CHAT_LOADING,
  CHAT_MARK_MESSAGES_READ,
  CHAT_MESSAGE_SENT,
  CHAT_MESSAGES,
  CHAT_MESSAGES_LOADING,
  CHAT_SEND_LOADING,
  CHAT_SOCKET_READY,
  CHAT_MESSAGE_RECEIVED,
  CHAT_UNREAD_COUNT,
} from "./type";

const getResponseMessage = (response) =>
  response?.data?.message ||
  response?.data?.data?.message ||
  response?.response?.data?.message ||
  response?.response?.data?.data?.message ||
  "";

const isSocketAckSuccessful = (response) =>
  response?.ok === true ||
  response?.success === true ||
  (Number(response?.status) >= 200 && Number(response?.status) < 300);

const getSocketAckError = (response, fallback) => {
  const message =
    response?.error?.message ||
    response?.message?.text ||
    response?.message ||
    response?.error ||
    fallback;
  return typeof message === "string" ? message : fallback;
};

const logoutSocketSession = () => {
  clearAuthenticationStorage();
  if (window.location.pathname !== "/") window.location.replace("/");
};

const getContainers = (response) => {
  const body = response?.data || {};
  return [body?.data?.data, body?.data, body].filter(
    (value) => value !== undefined && value !== null,
  );
};

const getUnreadCountValue = (response) => {
  const keys = [
    "unreadCount",
    "unreadMessageCount",
    "unreadMessagesCount",
    "totalUnreadCount",
    "totalUnread",
    "unread_count",
    "unread_message_count",
    "count",
  ];
  const pending = [response?.data, response?.data?.data, response?.data?.data?.data];
  const visited = new Set();

  while (pending.length) {
    const value = pending.shift();
    if (!value || typeof value !== "object" || visited.has(value)) continue;
    visited.add(value);

    for (const key of keys) {
      const candidate = value[key];
      if (candidate !== undefined && candidate !== null && candidate !== "") {
        const count = Number(candidate);
        if (Number.isFinite(count) && count >= 0) return count;
      }
    }

    Object.values(value).forEach((nested) => {
      if (nested && typeof nested === "object") pending.push(nested);
    });
  }

  if (typeof response?.data === "number" && response.data >= 0) {
    return response.data;
  }
  return null;
};

export const getChatUnreadCountAction = () => async (dispatch) => {
  try {
    const response = await chatService.getUnreadCount();
    const status = response?.status || response?.response?.status;
    const count = getUnreadCountValue(response);
    if (status >= 200 && status < 300 && count !== null) {
      dispatch({ type: CHAT_UNREAD_COUNT, payload: count });
    }
    return response;
  } catch (error) {
    return error;
  }
};

export const markChatMessagesReadAction = (inquiryId) => async (dispatch) => {
  const id = getInquiryMongoId(inquiryId);
  if (!id) return { success: false, error: "A valid inquiry ID is required." };

  try {
    const response = await chatService.markMessagesRead(id);
    const status = response?.status ?? response?.response?.status;
    if (status >= 200 && status < 300) {
      dispatch({
        type: CHAT_MARK_MESSAGES_READ,
        payload: { inquiryId: id },
      });
    }
    return response;
  } catch (error) {
    return error;
  } finally {
    dispatch(getChatUnreadCountAction());
  }
};

const getChatOwner = (response) => {
  const candidates = getContainers(response).flatMap((container) => [
    container?.vendor,
    container?.currentVendor,
    container?.user,
    container?.currentUser,
    container?.vendorProfile,
    container?.profile,
    container?.data?.vendor,
    container?.data?.user,
    container,
  ]);
  return (
    candidates.find(
      (value) =>
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        value.name &&
        (value.username || value.email || value._id || value.id),
    ) || null
  );
};
const getCollection = (response, keys) => {
  const containers = getContainers(response);
  const items =
    containers
      .flatMap((container) => {
        if (Array.isArray(container)) return [container];
        return keys.map((key) => container?.[key]).filter(Array.isArray);
      })
      .find(Array.isArray) || [];
  const data =
    containers.find(
      (container) =>
        container && typeof container === "object" && !Array.isArray(container),
    ) || {};
  const pagination =
    containers
      .map((container) => container?.pagination || container?.meta)
      .find(Boolean) || {};
  return { items, data, pagination, body: response?.data || {} };
};

const normalizePagination = (
  pagination,
  data,
  body,
  page,
  limit,
  itemCount,
) => {
  const currentPage =
    Number(
      pagination.page ||
        pagination.currentPage ||
        pagination.current_page ||
        data.page ||
        body.page,
    ) || Number(page);
  const pageSize =
    Number(
      pagination.limit ||
        pagination.pageSize ||
        pagination.perPage ||
        data.limit,
    ) || Number(limit);
  const reportedTotal =
    pagination.total ??
    pagination.totalCount ??
    pagination.totalItems ??
    pagination.totalRecords ??
    pagination.totalDocs ??
    pagination.count ??
    data.total ??
    data.totalCount ??
    data.totalItems ??
    data.totalRecords ??
    data.totalDocs ??
    body.total ??
    body.totalCount ??
    body.totalDocs;
  const reportedPages = Number(
    pagination.totalPages ||
      pagination.total_pages ||
      pagination.pages ||
      data.totalPages ||
      body.totalPages,
  );
  const total =
    reportedTotal !== undefined && reportedTotal !== null
      ? Number(reportedTotal) || 0
      : reportedPages
        ? Math.max((reportedPages - 1) * pageSize + itemCount, itemCount)
        : (currentPage - 1) * pageSize + itemCount;
  const totalPages =
    reportedPages ||
    (reportedTotal !== undefined && reportedTotal !== null
      ? Math.max(Math.ceil(total / pageSize), 1)
      : Math.max(currentPage + (itemCount === pageSize ? 1 : 0), 1));

  return {
    ...pagination,
    page: currentPage,
    limit: pageSize,
    total,
    totalPages,
  };
};

const firstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getId = (value) => {
  if (value && typeof value === "object")
    return firstValue(value._id, value.id, value.inquiryId);
  return value;
};

const toInquiryId = (value) =>
  typeof value === "string" || typeof value === "number"
    ? String(value).trim()
    : "";

const isMongoInquiryId = (value) =>
  /^[a-f0-9]{24}$/i.test(String(value || "").trim());

const getInquiryMongoId = (value) => {
  if (!value || typeof value !== "object") {
    return toInquiryId(value);
  }

  const nestedInquiry =
    value.inquiry && typeof value.inquiry === "object"
      ? value.inquiry
      : value.inquiryId && typeof value.inquiryId === "object"
        ? value.inquiryId
        : null;
  const isChatRecord = Boolean(
    value.chatType ||
      value.lastMessage ||
      value.latestMessage ||
      value.messages ||
      value.displayInquiryId ||
      value.admin ||
      value.vendor,
  );

  return toInquiryId(
    firstValue(
      value.inquiryMongoId,
      nestedInquiry?._id,
      value.inquiry?._id,
      value.inquiry_id?._id,
      value._id && !isChatRecord ? value._id : "",
      typeof value.inquiryId === "string" ? value.inquiryId : "",
    ),
  );
};

const getMessageInquiryId = (message) => {
  const payloads = [
    message,
    message?.chatMessage,
    message?.data,
    message?.data?.chatMessage,
    message?.payload,
    message?.payload?.chatMessage,
    message?.message,
  ];
  const candidate = toInquiryId(
    firstValue(
      ...payloads.flatMap((payload) => [
        // The nested inquiry document is authoritative. A chat's own _id
        // and a display inquiry number must never be used for room matching.
        getId(payload?.inquiry),
        getId(payload?.inquiryId),
        getId(payload?.inquiry_id),
        payload?.inquiryID,
        getId(payload?.chat?.inquiry),
        getId(payload?.chat?.inquiryId),
      ]),
    ),
  );
  return isMongoInquiryId(candidate) ? candidate : "";
};

const getSocketMessageObject = (response) =>
  [
    response?.chatMessage,
    response?.sentMessage,
    response?.data?.chatMessage,
    response?.data?.sentMessage,
    response?.data?.data?.chatMessage,
    response?.data?.data?.sentMessage,
    response?.data?.message,
    response?.message,
  ].find((candidate) => candidate && typeof candidate === "object") || null;

const waitForSocketConnection = (socket, timeoutMs = 15000) =>
  new Promise((resolve, reject) => {
    if (!socket) {
      reject(new Error("Live messaging is not available."));
      return;
    }
    if (socket.connected) {
      resolve();
      return;
    }

    let timeoutId;
    const cleanup = () => {
      clearTimeout(timeoutId);
      socket.off("connect", handleConnect);
      socket.off("connect_error", handleConnectError);
    };
    const handleConnect = () => {
      cleanup();
      resolve();
    };
    const handleConnectError = (error) => {
      cleanup();
      reject(error || new Error("Unable to connect to live messaging."));
    };

    timeoutId = setTimeout(() => {
      cleanup();
      reject(new Error("Unable to connect to live messaging."));
    }, timeoutMs);
    socket.on("connect", handleConnect);
    socket.on("connect_error", handleConnectError);
  });

const waitForInquiryRoom = async (socket) => {
  await waitForSocketConnection(socket);
  if (!socket?.inquiryRoomReady) return;
  const response = await socket.inquiryRoomReady;
  if (!isSocketAckSuccessful(response)) {
    throw new Error(
      getSocketAckError(response, "Unable to join this inquiry conversation."),
    );
  }
};

const getName = (value) => {
  if (typeof value === "string") return value;
  if (!value || typeof value !== "object") return "";
  return (
    firstValue(
      value.name,
      value.companyName,
      value.username,
      value.email,
      value.title,
      value.subject,
    ) || ""
  );
};

const makeInitials = (name) =>
  String(name || "IN")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const parseAttachmentMessage = (value) => {
  if (typeof value !== "string") return null;
  const separator = "\n\nAttachment: ";
  const separatorIndex = value.lastIndexOf(separator);
  const attachmentOnly = value.startsWith("Attachment: ");
  if (separatorIndex < 0 && !attachmentOnly) return null;

  const text = separatorIndex >= 0 ? value.slice(0, separatorIndex) : "";
  const attachmentLine =
    separatorIndex >= 0
      ? value.slice(separatorIndex + separator.length)
      : value.slice("Attachment: ".length);
  const urlStart = attachmentLine.lastIndexOf(" (");
  if (urlStart < 0 || !attachmentLine.endsWith(")")) return null;

  const url = attachmentLine.slice(urlStart + 2, -1);
  if (!/^https?:\/\//i.test(url)) return null;

  let fileName = attachmentLine.slice(0, urlStart);
  let fileSize = 0;
  const sizeEnd = fileName.lastIndexOf("]");
  if (sizeEnd === fileName.length - 1) {
    const sizeStart = fileName.lastIndexOf(" [");
    if (sizeStart >= 0) {
      fileSize = Number(fileName.slice(sizeStart + 2, sizeEnd)) || 0;
      fileName = fileName.slice(0, sizeStart);
    }
  }

  const isImage = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i.test(fileName);
  return {
    text,
    attachment: {
      url,
      fileName,
      fileSize,
      type: isImage ? "image" : "file",
    },
  };
};

const resolveAttachmentUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) return "";
  const url = value.trim();
  if (/^https?:\/\//i.test(url)) return url;
  try {
    return new URL(url, backendUrl).toString();
  } catch {
    return url;
  }
};

const normalizeAttachment = (rawAttachment, item = {}) => {
  if (!rawAttachment || typeof rawAttachment !== "object") return null;
  const fileName = String(
    firstValue(
      rawAttachment.originalName,
      rawAttachment.fileName,
      rawAttachment.name,
      item.fileName,
      "Attachment",
    ),
  );
  const mimeType = String(
    firstValue(
      rawAttachment.mimeType,
      rawAttachment.type,
      rawAttachment.contentType,
      "",
    ),
  ).toLowerCase();
  const url = resolveAttachmentUrl(
    firstValue(
      rawAttachment.url,
      rawAttachment.fileUrl,
      rawAttachment.downloadUrl,
      rawAttachment.location,
      rawAttachment.path,
      "",
    ),
  );
  return {
    ...rawAttachment,
    url,
    fileName,
    fileSize:
      Number(
        firstValue(
          rawAttachment.fileSize,
          rawAttachment.size,
          item.fileSize,
          0,
        ),
      ) || 0,
    type:
      mimeType.includes("image") ||
      /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i.test(fileName)
        ? "image"
        : "file",
  };
};

const normalizeChat = (chat, requestedInquiryId = "") => {
  const inquiry =
    chat?.inquiry && typeof chat.inquiry === "object"
      ? chat.inquiry
      : chat?.inquiryId && typeof chat.inquiryId === "object"
        ? chat.inquiryId
        : {};
  const partner =
    chat?.admin ||
    chat?.administrator ||
    chat?.recipient ||
    chat?.user ||
    chat?.client ||
    chat?.buyer ||
    chat?.customer ||
    inquiry?.admin ||
    inquiry?.administrator ||
    inquiry?.client ||
    inquiry?.buyer ||
    inquiry?.customer ||
    {};
  const inquiryMongoId = toInquiryId(
    firstValue(
      chat?.inquiryMongoId,
      getId(chat?.inquiry),
      getId(inquiry),
      getId(chat?.inquiry_id),
    ),
  );
  const displayInquiryId =
    [
      chat?.displayInquiryId,
      inquiry?.inquiryId,
      inquiry?.displayInquiryId,
      inquiry?.referenceNumber,
      inquiry?.code,
      chat?.reference,
      chat?.inquiryID,
      typeof chat?.inquiryId === "string" ? chat.inquiryId : "",
      requestedInquiryId,
    ]
      .map(toInquiryId)
      .find((candidate) => candidate && !isMongoInquiryId(candidate)) || "";
  const id =
    inquiryMongoId ||
    toInquiryId(
      firstValue(
        requestedInquiryId,
        getId(chat?.inquiryId),
        getId(inquiry),
        getId(chat?.inquiry_id),
        chat?.inquiryID,
      ),
    );
  if (!id) return null;
  const name =
    getName(partner) ||
    firstValue(
      chat?.clientName,
      chat?.buyerName,
      chat?.participantName,
      inquiry.subject,
      inquiry.title,
      chat?.name,
      chat?.title,
    ) ||
    "Inquiry";
  const lastMessage =
    chat?.lastMessage ||
    chat?.latestMessage ||
    chat?.last_message ||
    chat?.latest_message ||
    {};
  const rawMessageText =
    typeof lastMessage === "string"
      ? lastMessage
      : firstValue(
          lastMessage.message,
          lastMessage.text,
          lastMessage.content,
          lastMessage.body,
        );
  const messageText =
    typeof rawMessageText === "string" || typeof rawMessageText === "number"
      ? String(rawMessageText)
      : firstValue(
          rawMessageText?.text,
          rawMessageText?.content,
          rawMessageText?.message,
        ) || "";
  const parsedLastMessage = parseAttachmentMessage(messageText);
  const lastAttachment = Array.isArray(lastMessage.attachments)
    ? lastMessage.attachments[0]
    : null;
  const lastAttachmentName = lastAttachment
    ? firstValue(
        lastAttachment.originalName,
        lastAttachment.fileName,
        "Attachment",
      )
    : "";
  const lastMessagePreview = parsedLastMessage
    ? [
        parsedLastMessage.text,
        `Attachment: ${parsedLastMessage.attachment.fileName}`,
      ]
        .filter(Boolean)
        .join(" · ")
    : [messageText, lastAttachmentName && `Attachment: ${lastAttachmentName}`]
        .filter(Boolean)
        .join(" · ");
  const timestamp = firstValue(
    lastMessage.createdAt,
    lastMessage.updatedAt,
    lastMessage.time,
    chat?.updatedAt,
    chat?.createdAt,
    chat?.time,
  );
  const unread =
    Number(
      firstValue(chat?.unreadCount, chat?.unread, chat?.unreadMessages, 0),
    ) || 0;

  return {
    ...chat,
    id,
    inquiryId: id,
    displayInquiryId,
    inquiryMongoId: inquiryMongoId || "",
    name,
    initials: makeInitials(name),
    role: firstValue(partner.role, partner.type, chat?.role, "Admin"),
    online: Boolean(chat?.online || partner?.online),
    lastMessage: lastMessagePreview || "No messages yet",
    time: formatTime(timestamp),
    unread,
  };
};

const normalizeMessage = (item, vendorId, index = 0, forceSent = false) => {
  const rawSender = item?.sender || item?.author || item?.from || {};
  const sender =
    rawSender && typeof rawSender === "object" ? rawSender : {};
  const senderId = String(
    firstValue(
      sender._id,
      sender.id,
      item?.senderId?._id,
      item?.senderId?.id,
      item?.senderId,
      item?.userId?._id,
      item?.userId?.id,
      item?.userId,
      item?.createdBy,
      typeof rawSender === "string" && isMongoInquiryId(rawSender)
        ? rawSender
        : "",
      "",
    ),
  ).trim();
  const senderType = String(
    firstValue(
      item?.senderType,
      item?.senderRole,
      item?.senderModel,
      sender.type,
      sender.role,
      typeof rawSender === "string" ? rawSender : "",
      "",
    ),
  ).toLowerCase();
  const currentVendorId = String(vendorId || "").trim();
  const hasComparableIds = Boolean(
    senderId &&
      currentVendorId &&
      (senderId === currentVendorId ||
        (isMongoInquiryId(senderId) && isMongoInquiryId(currentVendorId))),
  );
  const isCurrentVendor = hasComparableIds && senderId === currentVendorId;
  const sent =
    forceSent ||
    (hasComparableIds
      ? isCurrentVendor
      : Boolean(item?.isMine || item?.isVendor || item?.sentByVendor) ||
        ["outgoing", "sent", "vendor"].includes(
          String(item?.direction || "").toLowerCase(),
        ) ||
        senderType.includes("vendor"));
  const timestamp = firstValue(
    item?.createdAt,
    item?.updatedAt,
    item?.timestamp,
    item?.time,
  );
  const rawText =
    typeof item?.message === "string"
      ? item.message
      : firstValue(
          item?.text,
          item?.content,
          item?.body,
          item?.message?.text,
          item?.message?.content,
        ) || "";
  const parsedAttachment = parseAttachmentMessage(rawText);
  const rawAttachment =
    item?.attachment ||
    item?.file ||
    item?.media ||
    (Array.isArray(item?.attachments) ? item.attachments[0] : null) ||
    (["image", "file"].includes(String(item?.type || "").toLowerCase()) &&
    item?.url
      ? item
      : null);
  const attachment =
    parsedAttachment?.attachment || normalizeAttachment(rawAttachment, item);
  const attachments = Array.isArray(item?.attachments)
    ? item.attachments
        .map((entry) => normalizeAttachment(entry, item))
        .filter(Boolean)
    : attachment
      ? [attachment]
      : [];
  const text = parsedAttachment ? parsedAttachment.text : String(rawText);
  const messageType = ["image", "file"].includes(
    String(item?.type || "").toLowerCase(),
  )
    ? String(item.type).toLowerCase()
    : "text";

  return {
    ...item,
    id: String(firstValue(item?._id, item?.id, "message-" + index)),
    sender: sent ? "sent" : "received",
    type: messageType,
    text,
    ...(attachment ? { attachment } : {}),
    ...(attachments.length ? { attachments } : {}),
    time: formatTime(timestamp),
    createdAt: timestamp || new Date().toISOString(),
    status: sent ? "sent" : "",
  };
};

const getDetail = (response) => {
  const containers = getContainers(response);
  const data =
    containers
      .map(
        (container) =>
          container?.chat ||
          container?.inquiry ||
          container?.item ||
          container?.data ||
          container,
      )
      .find((value) => value && typeof value === "object") || null;
  return data;
};

const getMessageCollection = (response) =>
  getCollection(response, ["messages", "items", "results", "list", "data"]);

const getVendorId = (state) => {
  const authMe = state?.auth?.authMe || {};
  const candidates = [
    authMe.vendor,
    authMe.user,
    authMe.data?.vendor,
    authMe.data?.data?.vendor,
    authMe.data,
    authMe,
    state?.chat?.vendorProfile,
  ].filter((value) => value && typeof value === "object");
  const mongoId = candidates
    .map((vendor) => firstValue(vendor._id, vendor.id))
    .find((id) => isMongoInquiryId(id));
  if (mongoId) return String(mongoId);
  return String(
    candidates
      .map((vendor) => firstValue(vendor._id, vendor.id, vendor.vendorId))
      .find(Boolean) || "",
  );
};

export const getChatListAction =
  ({ page = 1, limit = 20 } = {}) =>
  async (dispatch) => {
    dispatch({ type: CHAT_LOADING, payload: true });
    dispatch({ type: CHAT_ERROR, payload: "" });

    try {
      const response = await chatService.getChats({ page, limit });
      const status = response?.status || response?.response?.status;

      if (status >= 200 && status < 300) {
        const { items, data, pagination, body } = getCollection(response, [
          "chats",
          "items",
          "results",
          "list",
          "data",
        ]);
        const vendorProfile = getChatOwner(response);
        dispatch({
          type: CHAT_LIST,
          payload: {
            items: items.map((chat) => normalizeChat(chat)).filter(Boolean),
            pagination: normalizePagination(
              pagination,
              data,
              body,
              page,
              limit,
              items.length,
            ),
            ...(vendorProfile ? { vendorProfile } : {}),
          },
        });
      } else {
        dispatch({
          type: CHAT_ERROR,
          payload:
            getResponseMessage(response) || "Unable to load conversations.",
        });
      }

      return response;
    } catch (error) {
      dispatch({
        type: CHAT_ERROR,
        payload: getResponseMessage(error) || "Unable to load conversations.",
      });
      return error;
    } finally {
      dispatch({ type: CHAT_LOADING, payload: false });
    }
  };

export const getChatAction = (inquiryId) => async (dispatch) => {
  const id = getInquiryMongoId(inquiryId);
  if (!id) {
    const error = "A valid inquiry ID is required to load this conversation.";
    dispatch({ type: CHAT_ERROR, payload: error });
    return { success: false, error };
  }
  dispatch({ type: CHAT_DETAIL_LOADING, payload: true });
  dispatch({ type: CHAT_ERROR, payload: "" });

  try {
    const response = await chatService.getChat(id);
    const status = response?.status || response?.response?.status;

    if (status >= 200 && status < 300) {
      const detail = getDetail(response);
      dispatch({
        type: CHAT_DETAIL,
        payload: detail ? normalizeChat(detail, id) : null,
      });
    } else {
      dispatch({
        type: CHAT_ERROR,
        payload: getResponseMessage(response) || "Unable to load conversation.",
      });
    }

    return response;
  } catch (error) {
    dispatch({
      type: CHAT_ERROR,
      payload: getResponseMessage(error) || "Unable to load conversation.",
    });
    return error;
  } finally {
    dispatch({ type: CHAT_DETAIL_LOADING, payload: false });
  }
};

export const getChatMessagesAction =
  (inquiryId, { page = 1, limit = 20 } = {}) =>
  async (dispatch, getState) => {
    const id = getInquiryMongoId(inquiryId);
    if (!id) {
      const error = "A valid inquiry ID is required to load messages.";
      dispatch({ type: CHAT_ERROR, payload: error });
      return { success: false, error };
    }
    dispatch({
      type: CHAT_MESSAGES_LOADING,
      payload: { inquiryId: id, loading: true },
    });
    const requestStartedAt = Date.now();

    try {
      const response = await chatService.getMessages(id, { page, limit });
      const status = response?.status || response?.response?.status;

      if (status >= 200 && status < 300) {
        const { items, data, pagination, body } =
          getMessageCollection(response);
        const vendorId = getVendorId(getState());
        const chatItems = items.filter((item) => {
          const messageInquiryId = getMessageInquiryId(item);
          return !messageInquiryId || messageInquiryId === id;
        });
        dispatch({
          type: CHAT_MESSAGES,
          payload: {
            inquiryId: id,
            page: Number(page),
            requestStartedAt,
            items: chatItems.map((item, index) =>
              normalizeMessage(item, vendorId, index),
            ),
            pagination: normalizePagination(
              pagination,
              data,
              body,
              page,
              limit,
              items.length,
            ),
          },
        });
      } else {
        dispatch({
          type: CHAT_ERROR,
          payload: getResponseMessage(response) || "Unable to load messages.",
        });
      }

      return response;
    } catch (error) {
      dispatch({
        type: CHAT_ERROR,
        payload: getResponseMessage(error) || "Unable to load messages.",
      });
      return error;
    } finally {
      dispatch({
        type: CHAT_MESSAGES_LOADING,
        payload: { inquiryId: id, loading: false },
      });
    }
  };

export const sendChatMessageAction =
  (inquiryId, message, file = null) =>
  async (dispatch, getState) => {
    const id = getInquiryMongoId(inquiryId);
    const text = String(message || "").trim();
    if (!id || (!text && !file)) {
      const error = !id
        ? "A valid inquiry ID is required to send a message."
        : "Enter a message or attach a file before sending.";
      dispatch({ type: CHAT_ERROR, payload: error });
      return { success: false, error };
    }
    dispatch({
      type: CHAT_SEND_LOADING,
      payload: { inquiryId: id, loading: true },
    });
    dispatch({ type: CHAT_ERROR, payload: "" });

    try {
      const response = await chatService.sendMessage(
        id,
        { message: text },
        file,
      );
      const status = response?.status || response?.response?.status;

      if (status >= 200 && status < 300) {
        const containers = getContainers(response);
        const responseMessage =
          getSocketMessageObject(response) ||
          containers
            .map(
              (container) =>
                container?.sentMessage ||
                container?.chatMessage ||
                container?.message ||
                container?.item ||
                container?.data,
            )
            .find((value) => value && typeof value === "object");
        const normalized = normalizeMessage(
          responseMessage || {
            message: text,
            inquiryId: id,
            createdAt: new Date().toISOString(),
          },
          getVendorId(getState()),
          Date.now(),
          true,
        );
        dispatch({
          type: CHAT_MESSAGE_SENT,
          payload: { inquiryId: id, message: normalized },
        });
        return { success: true, message: normalized };
      }

      const errorMessage =
        getResponseMessage(response) || "Unable to send message.";
      dispatch({ type: CHAT_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    } catch (error) {
      const errorMessage =
        getResponseMessage(error) ||
        error?.message ||
        "Unable to send message.";
      dispatch({ type: CHAT_ERROR, payload: errorMessage });
      return { success: false, error: errorMessage };
    } finally {
      dispatch({
        type: CHAT_SEND_LOADING,
        payload: { inquiryId: id, loading: false },
      });
    }
  };
export const connectInquirySocketAction = (inquiry) => (dispatch, getState) => {
  const id = getInquiryMongoId(inquiry);
  if (!id) {
    const error = "A valid inquiry ID is required for live messaging.";
    dispatch({ type: CHAT_ERROR, payload: error });
    return null;
  }
  const token = localStorage.getItem("authToken");
  dispatch({
    type: CHAT_SOCKET_READY,
    payload: { inquiryId: id, ready: false },
  });

  if (!token) {
    dispatch({
      type: CHAT_ERROR,
      payload: "Your session has expired. Please sign in again.",
    });
    logoutSocketSession();
    return null;
  }

  const socket = chatSocketService.createChatSocket(token);

  socket.on("connect", () => {
    socket.inquiryRoomReady = new Promise((resolve) => {
      socket.resolveInquiryRoom = resolve;
    });
    chatSocketService.joinInquiryRoom(socket, id, (response) => {
      socket.resolveInquiryRoom?.(response);
      socket.resolveInquiryRoom = null;
      if (!isSocketAckSuccessful(response)) {
        dispatch({
          type: CHAT_SOCKET_READY,
          payload: { inquiryId: id, ready: false },
        });
        dispatch({
          type: CHAT_ERROR,
          payload: getSocketAckError(
            response,
            "Unable to join this inquiry conversation.",
          ),
        });
        return;
      }

      dispatch({
        type: CHAT_SOCKET_READY,
        payload: { inquiryId: id, ready: true },
      });
      dispatch({ type: CHAT_ERROR, payload: "" });
    });
  });

  socket.on("inquiry_message", (rawMessage) => {
    const eventInquiryId = getMessageInquiryId(rawMessage);
    // A socket is joined to one inquiry room. Some API/socket adapters wrap
    // the message in `chatMessage` and omit the inquiry field at the outer
    // level, so only reject an event when an explicit different ID is present.
    if (eventInquiryId && eventInquiryId !== id) return;

    const messagePayload =
      getSocketMessageObject(rawMessage) || rawMessage?.data || rawMessage;
    const message = normalizeMessage(
      messagePayload,
      getVendorId(getState()),
      Date.now(),
    );
    dispatch({
      type: CHAT_MESSAGE_RECEIVED,
      payload: { inquiryId: id, message },
    });
    dispatch(getChatUnreadCountAction());
  });

  socket.on("disconnect", () => {
    dispatch({
      type: CHAT_SOCKET_READY,
      payload: { inquiryId: id, ready: false },
    });
  });

  socket.on("connect_error", (error) => {
    socket.resolveInquiryRoom?.({
      ok: false,
      message: error?.message || "Unable to connect to live messaging.",
    });
    socket.resolveInquiryRoom = null;
    const errorMessage =
      error?.message || "Unable to connect to live messaging.";
    dispatch({
      type: CHAT_SOCKET_READY,
      payload: { inquiryId: id, ready: false },
    });
    dispatch({ type: CHAT_ERROR, payload: errorMessage });

    if (/invalid .*token|token is required|expired/i.test(errorMessage)) {
      logoutSocketSession();
    }
  });

  socket.connect();
  return socket;
};

export const disconnectInquirySocketAction =
  (socket, inquiry) => (dispatch) => {
    const id = getInquiryMongoId(inquiry);
    chatSocketService.disconnectChatSocket(socket);
    if (!id) return;
    dispatch({
      type: CHAT_SOCKET_READY,
      payload: { inquiryId: id, ready: false },
    });
  };

export const sendChatSocketMessageAction =
  (socket, inquiryId, message, file = null) =>
  async (dispatch, getState) => {
    const id = getInquiryMongoId(inquiryId);
    const text = String(message || "").trim();
    if (!id || (!text && !file)) {
      const error = !id
        ? "A valid inquiry ID is required to send a message."
        : "Enter a message or attach a file before sending.";
      dispatch({ type: CHAT_ERROR, payload: error });
      return { success: false, error };
    }

    dispatch({
      type: CHAT_SEND_LOADING,
      payload: { inquiryId: id, loading: true },
    });
    dispatch({ type: CHAT_ERROR, payload: "" });

    try {
      // Socket.IO carries text messages. Attachments use the message API so
      // the actual file is posted as multipart/form-data in `attachments`.
      if (file) {
        return dispatch(sendChatMessageAction(id, text, file));
      }

      await waitForInquiryRoom(socket);
      const response = await new Promise((resolve) => {
        chatSocketService.sendInquiryMessage(socket, id, text, resolve);
      });

      if (!isSocketAckSuccessful(response)) {
        const error = getSocketAckError(response, "Unable to send message.");
        dispatch({ type: CHAT_ERROR, payload: error });
        return { success: false, error };
      }

      const rawMessage = getSocketMessageObject(response) || {
        message: text,
        createdAt: new Date().toISOString(),
      };
      const normalized = normalizeMessage(
        rawMessage,
        getVendorId(getState()),
        Date.now(),
        true,
      );
      dispatch({
        type: CHAT_MESSAGE_SENT,
        payload: { inquiryId: id, message: normalized },
      });
      return { success: true, message: normalized };
    } catch (error) {
      // Keep the message send usable if Socket.IO is temporarily unavailable.
      // The API uses the same inquiry ID and stores text through FormData.
      if (!file) {
        const fallback = await dispatch(sendChatMessageAction(id, text));
        if (fallback?.success) return fallback;
      }
      const messageText =
        getResponseMessage(error) ||
        error?.message ||
        "Unable to send message.";
      dispatch({ type: CHAT_ERROR, payload: messageText });
      return { success: false, error: messageText };
    } finally {
      dispatch({
        type: CHAT_SEND_LOADING,
        payload: { inquiryId: id, loading: false },
      });
    }
  };
